import moment from 'moment';

/**
 * Format the time delta between two dates into a human-readable string
 * @param fromDate - Start date
 * @param toDate - End date
 * @returns Formatted delta string like "+2d 3h" or "-5m 30s"
 */
export function formatDelta(fromDate: string | Date, toDate: string | Date): string {
    const from = moment(fromDate);
    const to = moment(toDate);
    const diff = to.diff(from);

    if (Math.abs(diff) < 1000) return 'same time';

    const duration = moment.duration(Math.abs(diff));
    const isAfter = diff > 0;

    const parts: string[] = [];

    const days = Math.floor(duration.asDays());
    if (days > 0) {
        parts.push(`${days}d`);
    }

    const hours = duration.hours();
    if (hours > 0 && days < 7) {
        parts.push(`${hours}h`);
    }

    const minutes = duration.minutes();
    if (minutes > 0 && days === 0 && hours < 24) {
        parts.push(`${minutes}m`);
    }

    const seconds = duration.seconds();
    if (seconds > 0 && days === 0 && hours === 0 && minutes < 5) {
        parts.push(`${seconds}s`);
    }

    if (parts.length === 0) {
        return 'same time';
    }

    const deltaStr = parts.join(' ');
    return isAfter ? `+${deltaStr}` : `-${deltaStr}`;
}

/**
 * Format a time delta relative to a due time
 * @param dueTime - The due date/time
 * @param actualTime - The actual date/time
 * @returns Formatted string like "2h 30m after due time" or "5m before due time"
 */
export function formatDeltaRelativeToDue(dueTime: string | Date, actualTime: string | Date): string {
    const due = moment(dueTime);
    const actual = moment(actualTime);
    const diff = actual.diff(due);

    if (Math.abs(diff) < 1000) return 'at due time';

    const duration = moment.duration(Math.abs(diff));
    const isAfter = diff > 0;

    const parts: string[] = [];

    const days = Math.floor(duration.asDays());
    if (days > 0) {
        parts.push(`${days}d`);
    }

    const hours = duration.hours();
    if (hours > 0) {
        parts.push(`${hours}h`);
    }

    const minutes = duration.minutes();
    if (minutes > 0 && days === 0) {
        parts.push(`${minutes}m`);
    }

    const seconds = duration.seconds();
    if (seconds > 0 && days === 0 && hours === 0 && minutes === 0) {
        parts.push(`${seconds}s`);
    }

    if (parts.length === 0) {
        return isAfter ? 'just after due time' : 'just before due time';
    }

    const durationStr = parts.join(' ');
    return isAfter ? `${durationStr} after due time` : `${durationStr} before due time`;
}
